import type { ReactNode } from 'react';
import { OUTREACH_OUTLIER_OTHER_TEAM_DAYS } from '@aegislab/shared';
import { Button, CheckIcon, Input, Select, WarningIcon } from '@/shared/ui/atoms';
import type { OutreachBulkPreviewResponse } from '@/entities/outreach-candidate';
import * as s from './SendConfirmPanel.css';

/** 줄의 고치기 버튼이 돌아가는 단계. 덮개가 단계 상태를 든다. */
export type SendConfirmStep = 'candidates' | 'composer';

/** 지금 발송 · 예약 발송 (96445435). */
export type SendMode = 'now' | 'later';

/** 화면이 받는 예약 — 현지 벽시계(`YYYY-MM-DDTHH:mm`)와 시간대. 실제 시각은 서버가 바꾼다. */
export interface ScheduleDraft {
  local: string;
  timeZone: string;
}

export type SharedAddressGroup = OutreachBulkPreviewResponse['sharedAddressGroups'][number];

export interface SendConfirmPanelProps {
  preview: OutreachBulkPreviewResponse;
  /**
   * 작성기에서 고른 스티비 주소록과 보내는 사람. 둘 중 하나라도 비면 줄이 ⚠ 가 된다 —
   * 로그인 계정 주소로 대신 채우지 않는다(주소록마다 인증된 발신자만 스티비가 보낸다).
   */
  sender: { listName: string | null; name: string; email: string };
  /** 테스트 메일을 받는 곳 — 누른 사람의 로그인 계정이다. 보내는 사람과 다를 수 있다. */
  testRecipient: string;
  subject: string;
  hasBody: boolean;
  hasImage: boolean;
  productUrlDiffers: boolean;
  /** 크리에이터가 여는 응답용 폼 주소. 캠페인에 폼이 없으면 `null`. */
  formUrl: string | null;
  /**
   * 이번 발송에 한해 따로 보내기로 푼 묶음. 풀린 묶음은 서버 미리보기의 `sharedAddressGroups` 에서
   * 사라지므로, 되돌릴 줄을 남기려고 컨테이너가 기억해 넘긴다.
   */
  splitGroups: readonly SharedAddressGroup[];
  /**
   * 🔴 **지금 화면에 이 조작이 없다**(결정 2026-09-29). 「같은 주소를 쓰는 후보」 카드를 빼면서
   * 유일한 조작 자리가 사라졌다 — IA §3-3 의 「이번 발송에 한해 크리에이터별 발송으로 바꾼다」가
   * 닿을 데가 없다는 뜻이다. 기본 동작(주소당 한 통)과 서버 계약은 그대로라 카드만 도로 붙이면
   * 되살아나므로 이 자리를 지우지 않는다.
   */
  onToggleSplit: (group: SharedAddressGroup) => void;
  /** 「이미 답한 사람 다시 넣기」 칸(12bcfe19). 컨테이너가 명단 훅을 들고 넘긴다. */
  reincludeSlot?: ReactNode;
  /** 「다른 팀이 최근 연락함」을 이번 발송에만 다시 넣는 칸. */
  otherTeamReincludeSlot?: ReactNode;
  onGoTo: (step: SendConfirmStep) => void;
  onSend: () => void;
  sending?: boolean;
  /** 눌러도 보낼 수 없는 이유. 있으면 발송 버튼을 막고 이 문장을 보인다. */
  sendBlockedReason?: string | null;
  /** 방금 누른 발송이 실패한 이유. */
  sendError?: string | null;
  onSendTest: () => void;
  testSendState?: 'idle' | 'sending' | 'sent' | 'error';
  mode: SendMode;
  onModeChange: (mode: SendMode) => void;
  schedule: ScheduleDraft;
  onScheduleChange: (next: ScheduleDraft) => void;
  /** 고를 수 있는 시간대. 기본값(발송 나라의 시간대)이 들어 있어야 한다. */
  timeZoneOptions: readonly string[];
  onReserve: () => void;
}

const numberFormat = new Intl.NumberFormat('ko-KR');
const n = (value: number) => numberFormat.format(value);

const TEST_LABEL: Record<NonNullable<SendConfirmPanelProps['testSendState']>, string> = {
  idle: '테스트 메일 보내기',
  sending: '테스트 메일 보내는 중',
  sent: '테스트 메일을 다시 보내기',
  error: '테스트 메일 다시 보내기',
};

/**
 * 발송 확인 — IA `docs/design/ia/creator-recruitment-ia.md` §3-5 「발송 확인」, 승인 시안
 * ConfirmAfter(2026-09-24).
 *
 * 되돌릴 수 없는 마지막 관문이다. 맨 위 한 문장이 **몇 명 중 몇 명을 빼고 몇 통이 나가는가**를
 * 답하고, 아래 여섯 줄이 받는 사람 · 다시 볼 것 · 발신자 · 제목 · 내용 · 설문 버튼을 ✓/⚠ 로
 * 보인다. 줄마다 그 값을 고치는 단계로 돌아간다 — 여기서 직접 고치지 않는 것은 작성기와 후보
 * 선택이 각자 자동 저장을 들기 때문이다.
 *
 * 뺀 사유는 갈라 적는다(게이트 §3 불변 2). 원천에서 못 찾음은 삭제 신호가 아니다.
 *
 * **기존 구독자와 새로 등록할 주소를 미리 가르지 않는다.** 세려면 27만 건 주소록을 먼저 훑어야
 * 해서 이 화면이 그만큼 늦어진다(2026-09-23).
 */
export function SendConfirmPanel({
  preview,
  sender,
  testRecipient,
  subject,
  hasBody,
  hasImage,
  productUrlDiffers,
  formUrl,
  splitGroups,
  reincludeSlot = null,
  otherTeamReincludeSlot = null,
  onGoTo,
  onSend,
  sending = false,
  sendBlockedReason = null,
  sendError = null,
  onSendTest,
  testSendState = 'idle',
  mode,
  onModeChange,
  schedule,
  onScheduleChange,
  timeZoneOptions,
  onReserve,
}: SendConfirmPanelProps) {
  const later = mode === 'later';
  const nothingToSend = preview.sendCount === 0;
  const otherTeamRecentCount = preview.otherTeamRecentCount ?? null;
  const otherTeamReincludedCount = preview.reincluded.filter(
    (entry) => entry.reason === 'OTHER_TEAM_RECENT'
  ).length;
  // 서버가 주는 수는 아직 빠져 있는 사람이라, 다시 넣은 사람을 더해야 전체 인원이 된다.
  const otherTeamTotal =
    otherTeamRecentCount === null ? null : otherTeamRecentCount + otherTeamReincludedCount;
  const excludedReasons = [
    ['직접 제외', preview.manuallyExcludedCount],
    ['이미 답함', preview.defaultExcludedCount - (otherTeamRecentCount ?? 0)],
    ['다른 팀이 최근 연락함', otherTeamRecentCount ?? 0],
    ['재접촉 보류', preview.heldCount],
    ['연락 제외', preview.blockedCount],
    ['수신거부', preview.unsubscribedCount],
    ['반송', preview.hardBouncedCount],
    ['다른 담당자 발송·예약', preview.otherOwnerSentCount],
    ['이미 받음', preview.alreadySentCount],
    ['원천에서 못 찾음', preview.unresolvedCount],
    ['주소 없음', preview.noEmailCount],
  ] as const;
  const excludedCount = excludedReasons.reduce((sum, [, count]) => sum + count, 0);
  // 서버가 접은 묶음이 곧 한 통으로 나가는 묶음이다. 풀기로 한 묶음은 미리보기가 새로 오기 전에도 빼 둔다.
  const splitEmails = new Set(splitGroups.map((group) => group.email));
  const folded = preview.sharedAddressGroups.filter((group) => !splitEmails.has(group.email));
  const foldedGroups = folded.length;

  const contentWarnings = [
    hasImage ? null : '대표 이미지가 없습니다.',
    productUrlDiffers ? '제품 링크가 캠페인 기본값과 다릅니다.' : null,
    hasBody ? null : '본문이 비어 있습니다.',
  ].filter((text): text is string => text !== null);
  return (
    <div className={s.stack}>
      <div className={s.stepBody}>
        {/*
        단계 줄도 회차 번호도 여기 없다(2026-09-28). 탭 바로 아래 세 단계 공통 자리
        (`OutreachStepCrumbs`)로 올렸다 — 여기에만 있으면 앞 두 단계에서는 자기 위치를 알
        수 없었고, 그 줄이 서면서 같은 것이 한 화면에 두 번 보였다.
      */}
        <p className={s.summary} data-testid="send-confirm-summary">
          {preview.country}에서 고른 {n(preview.selectedCount)}명 중 {n(excludedCount)}명을 빼고,
          {preview.reincludedCount > 0 ? ` ${reincludedSummary(preview)}을 다시 넣어,` : ''}
          {foldedGroups > 0
            ? ` 같은 주소를 쓰는 계정 ${n(foldedGroups)}묶음은 한 통씩 보내`
            : ''}{' '}
          <strong className={s.summaryStrong}>{n(preview.sendCount)}통</strong>을 보냅니다.
        </p>

        <ul className={s.checklist}>
          <CheckRow
            term="받는 사람"
            ok={!nothingToSend}
            value={
              nothingToSend
                ? '보낼 수 있는 사람이 없습니다.'
                : preview.roundCount > 1
                  ? `${n(preview.sendCount)}통 · 1,000통씩 ${n(preview.roundCount)}회차`
                  : `${n(preview.sendCount)}통`
            }
            sub={
              excludedCount === 0
                ? '뺀 사람이 없습니다.'
                : excludedReasons
                    .filter(([, count]) => count > 0)
                    .map(([label, count]) => `${label} ${n(count)}`)
                    .join(' · ') +
                  (preview.reincludedCount > 0 ? ` · ${reincludedSummary(preview)} 다시 넣음` : '')
            }
            action={<EditButton label="대상 수정" onClick={() => onGoTo('candidates')} />}
          />
          <CheckRow
            term="다시 볼 것"
            ok={otherTeamTotal === 0}
            value={
              otherTeamTotal === null
                ? '다른 팀이 최근 연락했는지 확인하지 못했습니다.'
                : otherTeamTotal === 0
                  ? '다른 팀이 최근 연락한 사람은 없습니다.'
                  : otherTeamReincludedCount === 0
                    ? `다른 팀이 최근 연락한 ${n(otherTeamTotal)}명은 기본으로 뺐습니다.`
                    : `다른 팀이 최근 연락한 ${n(otherTeamTotal)}명 중 ${n(otherTeamReincludedCount)}명을 다시 넣었습니다.`
            }
            sub={`최근 ${OUTREACH_OUTLIER_OTHER_TEAM_DAYS}일 안에 다른 팀이 연락한 사람은 기본으로 빼고, 다시 넣기는 이번 발송에만 적용합니다.`}
            action={<EditButton label="다시 보기" onClick={() => onGoTo('candidates')} />}
          />
          <CheckRow
            term="발신자"
            ok={sender.listName !== null && sender.email !== ''}
            value={
              sender.listName === null || sender.email === ''
                ? '주소록과 보내는 사람을 작성기에서 골라 주세요.'
                : `${sender.listName} · ${sender.name} <${sender.email}>`
            }
            action={<EditButton label="수정" onClick={() => onGoTo('composer')} />}
          />
          <CheckRow
            term="제목"
            ok={subject.trim() !== ''}
            value={subject.trim() === '' ? '제목이 비어 있습니다.' : subject}
            action={<EditButton label="수정" onClick={() => onGoTo('composer')} />}
          />
          <CheckRow
            term="내용"
            ok={contentWarnings.length === 0}
            value={contentWarnings[0] ?? '대표 이미지가 있고 제품 링크는 캠페인 기본값입니다.'}
            sub={contentWarnings.slice(1).join(' ')}
            action={<EditButton label="수정" onClick={() => onGoTo('composer')} />}
          />
          <CheckRow
            term="설문 버튼"
            ok={formUrl !== null}
            value={
              formUrl === null
                ? '캠페인에 설문폼 링크가 없습니다.'
                : '캠페인 설문폼으로 연결됩니다.'
            }
            sub={
              formUrl === null
                ? '캠페인 정보에서 설문폼 응답 시트 링크를 먼저 넣어야 합니다.'
                : '목적지는 이 화면에서 바꿀 수 없습니다.'
            }
            action={
              formUrl === null ? null : (
                <a className={s.editLink} href={formUrl} target="_blank" rel="noreferrer">
                  보기
                </a>
              )
            }
          />
        </ul>
        {reincludeSlot}
        {otherTeamReincludeSlot}
      </div>

      <div className={s.modes}>
        <button
          type="button"
          className={later ? s.modeOff : s.modeOn}
          aria-pressed={!later}
          onClick={() => onModeChange('now')}
        >
          <strong>지금 발송</strong>
          <span className={s.modeSub}>누르는 순간 스티비에 등록하고 보냅니다.</span>
        </button>
        <button
          type="button"
          className={later ? s.modeOn : s.modeOff}
          aria-pressed={later}
          onClick={() => onModeChange('later')}
        >
          <strong>예약 발송</strong>
          <span className={s.modeSub}>고른 시각에 스티비가 보냅니다.</span>
        </button>
      </div>

      {later ? (
        <div className={s.schedule}>
          <div className={s.scheduleFields}>
            <Input
              type="datetime-local"
              label="예약 일시"
              inputSize="sm"
              value={schedule.local}
              onChange={(event) => onScheduleChange({ ...schedule, local: event.target.value })}
            />
            <Select
              label="시간대"
              selectSize="sm"
              value={schedule.timeZone}
              options={timeZoneOptions.map((zone) => ({ value: zone, label: zone }))}
              onChange={(event) => onScheduleChange({ ...schedule, timeZone: event.target.value })}
            />
          </div>
          {/* 설계 §3 — 30분 잠금은 스티비 마감이 아니라 제외와 재조회를 끝낼 여유다. */}
          <p className={s.scheduleNote}>
            예약 30분 전까지 답장하거나 폼을 낸 사람은 자동으로 빠집니다. 그 뒤에 들어온 사람은
            예정대로 받고, 발송 이력에 마감 후 도착으로 남습니다.
          </p>
        </div>
      ) : null}

      {sendBlockedReason ? <p className={s.blocked}>{sendBlockedReason}</p> : null}
      {sendError ? (
        <p className={s.blocked} role="alert">
          {sendError}
        </p>
      ) : null}

      {/*
        버튼 둘이 한 줄에서 양 끝으로 벌어지고, 안내 문장은 **그 아래 줄**이다(2026-09-30). 전에는
        셋이 한 줄이라 문장이 버튼 사이에 끼어 두 버튼의 거리가 문장 길이에 따라 흔들렸다.
      */}
      <div className={s.footer}>
        <div className={s.footerActions}>
          <Button
            variant="secondary"
            size="sm"
            onClick={onSendTest}
            disabled={testSendState === 'sending' || testRecipient === ''}
          >
            {TEST_LABEL[testSendState]}
          </Button>
          {nothingToSend ? (
            <Button variant="secondary" size="sm" onClick={() => onGoTo('candidates')}>
              후보 선택으로 돌아가기
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={later ? onReserve : onSend}
              disabled={sending || Boolean(sendBlockedReason)}
            >
              {sending
                ? later
                  ? '예약하는 중'
                  : '보내는 중'
                : `${n(preview.sendCount)}통 ${later ? '예약 발송' : '지금 발송'}`}
            </Button>
          )}
        </div>
        {/*
          테스트는 누른 사람의 Gmail 연결로 나가 스티비를 안 탄다 — 테스트가 왔다고 발송도 되리라
          믿으면 안 된다(2026-09-28 실측 1회차: 테스트는 도착, 발송은 발신자 거부로 정지).
        */}
        <span className={s.testNote}>
          {testSendState === 'sent'
            ? `${testRecipient} 로 테스트 메일을 보냈습니다.`
            : `${testRecipient} 로 보냅니다. 테스트는 개인 Gmail 로 나가 스티비 발신자 확인과는 무관합니다.`}
        </span>
      </div>
    </div>
  );
}

function reincludedSummary(preview: OutreachBulkPreviewResponse): string {
  const otherTeam = preview.reincluded.filter(
    (entry) => entry.reason === 'OTHER_TEAM_RECENT'
  ).length;
  const answered = preview.reincludedCount - otherTeam;
  if (otherTeam === 0) return `이미 답한 ${n(answered)}명`;
  if (answered === 0) return `다른 팀이 최근 연락한 ${n(otherTeam)}명`;
  return `이미 답한 ${n(answered)}명과 다른 팀이 최근 연락한 ${n(otherTeam)}명`;
}

/** 확인 줄 한 칸 — ✓/⚠ · 항목 · 값과 보조 문장 · 고치러 가는 버튼. */
function CheckRow({
  term,
  ok,
  value,
  sub,
  action,
}: {
  term: string;
  ok: boolean;
  value: string;
  sub?: string;
  action: ReactNode;
}) {
  return (
    <li className={s.checkRow} aria-label={term}>
      <span
        role="img"
        aria-label={ok ? '확인됨' : '확인 필요'}
        className={ok ? s.okMark : s.warnMark}
      >
        {ok ? <CheckIcon /> : <WarningIcon />}
      </span>
      <span className={s.checkTerm}>{term}</span>
      <span className={s.checkBody}>
        <span className={s.checkValue}>{value}</span>
        {sub ? <span className={s.checkSub}>{sub}</span> : null}
      </span>
      <span className={s.checkAction}>{action}</span>
    </li>
  );
}

function EditButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="ghost" size="xs" onClick={onClick}>
      {label}
    </Button>
  );
}
